import SPInspection from '@lib/spproject/SPInspection';
import type { Inspection } from '@lib/spproject/types';
import { Box, Divider, Typography, useTheme } from '@mui/material';
import type { IInspection } from '@store/api/inspectionEndpoints';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { memo } from 'react';
import { Pie, PieChart, ResponsiveContainer } from 'recharts';
import { PoiContainer } from './Checklist.styled';

interface ChecklistPointItemProps {
  poi: Inspection.PointOfInterest;
  template: IInspection;
  inspections: IInspection[];
  index: number;
  onClick: (poi: Inspection.PointOfInterest, template: IInspection) => void;
}

const ChecklistPointItem = memo<ChecklistPointItemProps>(({ poi, template, inspections, index, onClick }) => {
  const theme = useTheme();
  const noticeColors = useNoticeColors();

  const pois = inspections
    .map((i) => i.pointOfInterests?.find((p) => p.id === poi.id))
    .filter(Boolean) as Inspection.PointOfInterest[];

  const [okPOIs, errorPOIs, warningPOIs, notCheckedPOIs]: Inspection.PointOfInterest[][] = [
    [], [], [], []
  ];

  for (const poi of pois) {
    const status = SPInspection.getPOIStatus(poi);
    switch (status) {
      case 'Ok': okPOIs.push(poi); break;
      case 'Error': errorPOIs.push(poi); break;
      case 'Warning': warningPOIs.push(poi); break;
      case 'NotChecked': notCheckedPOIs.push(poi); break;
    }
  }

  return (
    <Box key={poi.id}>
      {index > 0 && <Divider />}
      <PoiContainer onClick={() => onClick(poi, template)}>
        <Typography variant="caption" color="text.secondary">
          #{poi.order}
        </Typography>

        <Box sx={{ width: 48, height: 48 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={[
                  { name: 'Ok', value: okPOIs.length, fill: noticeColors.Ok },
                  { name: 'Warning', value: warningPOIs.length, fill: noticeColors.Warning },
                  { name: 'Error', value: errorPOIs.length, fill: noticeColors.Error },
                  { name: 'NotChecked', value: notCheckedPOIs.length, fill: noticeColors.NotChecked }
                ]}
                cx="50%"
                cy="50%"
                innerRadius={12}
                outerRadius={22}
                paddingAngle={2}
                dataKey="value"
                stroke={theme.palette.divider}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            {poi.name}
          </Typography>
          {poi.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {poi.description}
            </Typography>
          )}
        </Box>
      </PoiContainer>
    </Box>
  );
});

export default ChecklistPointItem;
